/*
 * Ce raspunsuri ale service desk-ului a vazut deja omul.
 *
 * ---------------------------------------------------------------------------
 * DE CE IN BROWSER, SI NU PE SERVER
 * ---------------------------------------------------------------------------
 *
 * Api-ul trimite, pe fiecare tichet, `last_staff_comment_id` - numarul celui
 * mai nou comentariu public scris de echipa. El nu stie insa daca omul l-a
 * CITIT: asta e o parere despre om, si singurul loc unde se stie e ecranul pe
 * care el l-a deschis.
 *
 * Exista in baza un tabel `it_easeedesk_comment_reads`, dar `user_id` de acolo
 * primeste id-uri de angajati, scrise de portal. Omul din catalog e un contact
 * - alta tabela, alta numerotare. Puse la un loc fara sa se distinga, id-ul 412
 * al unui angajat si 412 al unui contact devin acelasi rand, si cineva ar marca
 * drept citite raspunsurile altcuiva. Tacut. Pana cand tabelul acela capata o
 * coloana care sa spuna ce fel de om e, semnul sta aici.
 *
 * Pretul, spus pe fata: e per browser. Pe alt calculator, clopotelul apare o
 * data si se stinge la prima deschidere. Pentru o atentionare e acceptabil -
 * nu e o evidenta, e un semn ca cineva ti-a scris.
 *
 * ---------------------------------------------------------------------------
 * DE CE UN NUMAR, SI NU O DATA
 * ---------------------------------------------------------------------------
 *
 * Id-urile cresc. O data ar fi cerut fus orar, ceas potrivit pe statie si
 * comparatii de siruri; un numar se compara cu `>`. Iar daca ceasul statiei e
 * in urma cu o zi - se intampla - datele ar fi mintit, numerele nu.
 */

const KEY = 'catalog.seenReplies';

type SeenMap = Record<string, number>;

function read(): SeenMap {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);

    /* Ce e in stocare a fost scris de o versiune mai veche, sau de nimeni.
       Un obiect stricat nu are voie sa opreasca ecranul. */
    if (typeof parsed !== 'object' || parsed === null) return {};

    return parsed as SeenMap;
  } catch {
    /* Fereastra privata, stocare oprita din setari, cota depasita: se merge
       fara memorie, adica se arata clopotelul. Mai bine o data in plus decat
       un ecran care nu porneste. */
    return {};
  }
}

function write(map: SeenMap): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(map));
  } catch {
    /* vezi mai sus */
  }
}

/** Numarul ultimului raspuns vazut pe tichetul asta. Zero cand niciunul. */
export function seenReply(ticketId: number | string): number {
  const value = read()[String(ticketId)];
  return typeof value === 'number' ? value : 0;
}

/** A scris echipa ceva ce omul n-a vazut inca? */
export function hasNewReply(ticketId: number | string, lastStaffCommentId: number | null | undefined): boolean {
  return (lastStaffCommentId ?? 0) > seenReply(ticketId);
}

/**
 * Tichetul a fost deschis: ce e pana aici e vazut.
 *
 * Se scrie numai in sus. Doua file deschise pe acelasi tichet, sau un raspuns
 * venit intre timp, nu au voie sa dea semnul inapoi si sa reaprinda clopotelul
 * pentru ceva ce s-a citit deja.
 */
export function markReplySeen(ticketId: number | string, lastStaffCommentId: number | null | undefined): void {
  const value = lastStaffCommentId ?? 0;
  const map = read();
  const key = String(ticketId);

  if ((map[key] ?? 0) >= value) return;

  map[key] = value;
  write(map);
}
